<template>
	<v-skeleton-loader
		v-if="loading && this.$route.params.id"
		v-bind="loader"
		width="100%"
		type="card"
	></v-skeleton-loader>
	<v-row v-else class="justify-center">
		<v-col md="12" sm="12">
			<v-form
				class="post-form pt-10"
				ref="form"
				v-model="valid"
				lazy-validation
				@submit.prevent="validateAndSubmit"
			>
				<v-row>
					<v-col class="post-form__outer pr-2 pl-2" cols="12">
						<v-text-field
							class="subtitle-2 post-form__title"
							placeholder="Post title"
							solo
							:value="fields.title"
							v-model="fields.title"
							:error-messages="field_errors.title"
							:rules="rules.title"
							required
						></v-text-field>
					</v-col>
				</v-row>
				<v-row>
					<v-col cols="12" class="pt-6 pr-0 pl-0 pr-2">
						<v-file-input
							solo
							class="subtitle-2 post-form__thumbnail"
							accept="image/*"
							label="Post thumbnail"
							v-model="fields.thumbnail"
						></v-file-input>
					</v-col>
				</v-row>
				<v-row>
					<v-col cols="12">
						<vue-editor
							id="editor"
							class="post-form__editor ma-0"
							v-model="fields.content"
							use-custom-image-handler
							v-on:image-added="handleImageAdded"
							:editorToolbar="customToolbar"
							placeholder="Enter text"
						></vue-editor>
						<!-- Hidden input in order to add validation for <vue-editor> -->
						<v-text-field
							class="subtitle-2 post-form__validation-field pt-0 mt-0"
							:value="fields.content"
							solo
							v-model="fields.content"
							:error-messages="field_errors.content"
							:rules="rules.content"
							required
						></v-text-field>
					</v-col>
				</v-row>
				<v-row>
					<v-col cols="12">
						<GmapAutocomplete
							:options="{ fields: ['geometry', 'formatted_address'] }"
							style="width: 100%"
							class="subtitle-2 d-flex pb-0"
							@place_changed="setPlace"
							:value="fields.address"
							placeholder="Please enter address..."
						/>
						<v-text-field
							class="subtitle-2 post-form__validation-field pt-1"
							:value="fields.address"
							solo
							v-model="fields.address"
							:error-messages="field_errors.address"
							:rules="rules.address"
							required
						>
						</v-text-field>
					</v-col>
				</v-row>
				<v-row>
					<v-col cols="12">
						<v-divider class="post-form__divider mb-5"></v-divider>
					</v-col>
				</v-row>
				<v-row>
					<v-col md="6" sm="12">
						<h4 class="post-form__heading">This post about:</h4>
						<v-chip-group
							v-model="fields.categories"
							:error-messages="field_errors.categories"
							:rules="rules.categories"
							multiple
							active-class="post-form__tag-active"
						>
							<v-chip
								class="post-form__tag"
								v-for="(category, index) in categoriesList"
								:key="index"
								:value="category.id"
							>
								{{ category.name }}
							</v-chip>
						</v-chip-group>
					</v-col>
					<v-col md="6" sm="12">
						<h4 class="post-form__heading">Please add tags:</h4>
						<TagForm />
						<v-skeleton-loader v-if="this.isTagsLoading" type="chip">
						</v-skeleton-loader>
						<v-chip-group
							v-else
							v-model="fields.tags"
							:error-messages="field_errors.tags"
							:rules="rules.tags"
							column
							multiple
							active-class="post-form__tag-active"
						>
							<v-chip
								class="post-form__tag"
								v-for="(tag, index) in tagsList.slice(0, tagCount)"
								:key="index"
								close
								:value="tag.id"
								@click:close="handleTagDelete(tag.id)"
							>
								{{ tag.name }}
							</v-chip>
						</v-chip-group>
						<div class="d-flex justify-center">
							<v-btn
								class="post-form__show-more-btn"
								v-if="tagsList.length > 8"
								@click="showMoreTags"
							>
								{{ isExpanded ? 'Show less' : 'Show more' }}
							</v-btn>
						</div>
					</v-col>
				</v-row>
				<v-row>
					<v-col cols="12">
						<v-divider class="post-form__divider mb-5"></v-divider>
					</v-col>
				</v-row>
				<v-row class="mt-0">
					<v-col cols="12">
						<p v-for="error in errors" :key="error" class="red--text">
							{{ error }}
						</p>
						<v-btn
							plain
							:loading="loading"
							class="text-none mr-4 mt-3 post-form__submit"
							:class="[fields.title.length > 0 ? 'post-form__submit-active' : '']"
							@click="validateAndSubmit"
						>
							{{ this.$route.params.id ? 'Edit' : 'Publish' }}
						</v-btn>
					</v-col>
				</v-row>
			</v-form>
		</v-col>
	</v-row>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import { ruleRequired, ruleLatitude, ruleLongitude } from '@/utils/rules'

import { VueEditor } from 'vue2-editor'
import TagForm from '@/components/Forms/TagForm'
import Form from '@/mixins/Form'

export default {
	name: 'PostForm',
	mixins: [Form],
	data: () => ({
		currentPost: null,
		tagCount: 8,
		isExpanded: false,
		fields: {
			id: null,
			title: '',
			content: '',
			tags: [],
			categories: [],
			date: '',
			thumbnail: undefined,
			imageUrl: '',
			lat: null,
			long: null,
			address: '',
		},
		customToolbar: [
			['bold'],
			['blockquote'],
			['underline'],
			[{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
			['image'],
		],
		loader: {
			class: 'mb-6',
			boilerplate: true,
			elevation: 2,
		},
		selectedTag: [],
		timer: 0,
	}),
	components: {
		VueEditor,
		TagForm,
	},
	props: {
		post: {
			type: Object,
			default: () => {},
		},
	},
	watch: {
		post: {
			handler(value) {
				if (value) {
					this.fields = this.post
				}
			},
			deep: true,
		},
		fields: {
			handler() {
				this.field_errors = {}
			},
			deep: true,
		},
	},
	computed: {
		...mapGetters('category', ['isCategoryLoading', 'categories']),
		...mapGetters('tag', ['isTagsLoading', 'tags']),
		...mapGetters('file', ['editorImage']),
		tagsList() {
			return this.tags
		},
		categoriesList() {
			return this.categories
		},
		editorImageURL() {
			return this.editorImage
		},
		rules() {
			return {
				title: [(v) => ruleRequired(v) || 'Field is required.'],
				content: [(v) => ruleRequired(v) || 'Field is required.'],
				address: [(v) => ruleRequired(v) || 'Field is required.'],
				lat: [(v) => ruleLatitude(v) || 'Please enter a valid latitude value'],
				long: [(v) => ruleLongitude(v) || 'Please enter a valid longitude value'],
			}
		},
	},
	created() {
		this.fetchTags()
		if (this.post) {
			this.fields = this.post
		}
	},
	methods: {
		...mapActions('post', ['addPost', 'updatePost']),
		...mapActions('tag', ['fetchTags', 'deleteTag']),
		...mapActions('file', ['addImage']),
		async handleImageAdded(file, Editor, cursorLocation, resetUploader) {
			let formData = new FormData()
			formData.append('file', file)
			await this.addImage(formData)
			Editor.insertEmbed(cursorLocation, 'image', this.editorImageURL)
			resetUploader()
		},
		showMoreTags() {
			if (this.isExpanded) {
				this.tagCount = 8
			} else {
				this.tagCount = this.tagsList.length - 1
			}
			this.isExpanded = !this.isExpanded
		},
		handleTagDelete(item) {
			this.deleteTag(item)
		},
		/**
		 * @param {Object} place PlaceResult object
		 */
		setPlace(place) {
			this.fields.lat = place.geometry.location.lat()
			this.fields.long = place.geometry.location.lng()
			this.fields.address = place.formatted_address
		},
		submitForm() {
			const data = new FormData()

			data.append('title', this.fields.title)
			data.append('content', this.fields.content)

			if (this.fields.address) {
				data.append('location_name', this.fields.address)
			}

			if (this.fields.lat) {
				data.append('latitude', this.fields.lat)
			}

			if (this.fields.long) {
				data.append('longitude', this.fields.long)
			}

			if (this.$route.params.id) {
				data.append('_method', 'PUT')
			}

			if (this.fields.thumbnail) {
				data.append('thumbnail', this.fields.thumbnail)
			}

			this.fields.tags.forEach((tagId) => data.append('tags[]', tagId))
			this.fields.categories.forEach((categoryId) => data.append('categories[]', categoryId))

			return this.$route.params.id
				? this.updatePost({ postId: this.$route.params.id, postData: data })
				: this.addPost(data)
		},
	},
}
</script>

<style lang="scss">
.post-form {
	&__title {
		margin: 0 0 31px;
		padding: 25px 12px;
		color: #000;
		height: 40px;
		transition: all 0.2s;
		letter-spacing: 0.4px;
		color: rgba(0, 0, 0, 0.8);
		font-family: 'Merriweather' !important;
		font-size: 20px !important;
		font-weight: bold !important;
		color: #202232;

		.v-input__slot {
			padding-left: 3px !important;
			box-shadow: none !important;
			border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
			border-radius: 0 !important;
		}
	}

	&__thumbnail {
		transition: all 0.2s;
		letter-spacing: 0.4px;
		font-family: 'Merriweather' !important;
		font-size: 10px !important;
		color: #202232;

		.v-input__slot {
			box-shadow: none !important;
			border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
			border-radius: 0 !important;
		}
	}

	&__heading {
		margin: 0 0 16px 5px;
		font: bold 14px/1.3 'Merriweather';
		letter-spacing: -0.28px;
	}

	&__title:focus {
		outline: none;
	}

	&__editor {
		display: flex;
		flex-direction: column;
		padding: 14px 23px;
		transition: all 0.2s;
		font: 17px/1.8 'Montserrat';
		letter-spacing: 0.4px;
	}

	&__validation-field {
		max-height: 12px !important;
		font-weight: bold !important;

		.v-text-field__details {
			padding-left: 0 !important;
		}

		.v-input__slot {
			display: none !important;
		}
	}

	&__divider {
		border-color: #000 !important;
		mix-blend-mode: normal;
		opacity: 0.1;
	}

	&__tag {
		height: 36px !important;
		font: 600 15px/1.2 'Montserrat' !important;
		letter-spacing: -0.018em;
		color: #4d536e !important;
		border-radius: 1.28em !important;
		background: #f8f8f8 !important;
		margin-right: 10px;
		margin-bottom: 1.53em;
		padding: 0 30px;
	}

	&__tag:hover,
	&__category:hover {
		cursor: pointer;
		transition: color 0.2s, background-color 0.2s, border-color 0.2s;
		color: white !important;
		background: #db7842 !important;
	}

	&__tag-active {
		transition: color 0.2s, background-color 0.2s, border-color 0.2s;
		color: white !important;
		background: #db7842 !important;
	}

	&__show-more-btn {
		border-radius: 24.5px;
		background-color: #db7842 !important;
		color: #fff !important;
		font-family: Montserrat;
		font-style: normal;
		width: 50%;
		font-weight: 900;
		font-size: 15px;
		line-height: 16px;
		text-align: center;

		&:hover {
			opacity: 0.7;
		}
	}

	&__submit {
		width: 191px;
		height: 38px;
		border-radius: 24.5px;
		background-color: #e5e5e5 !important;
		color: rgba(77, 83, 110, 1) !important;
		font-family: Montserrat;
		font-style: normal;
		font-weight: 900;
		font-size: 15px;
		line-height: 16px;
		text-align: center;

		&:hover {
			color: #fff !important;
			background-color: #db7842 !important;
			opacity: 1;
		}
	}

	&__submit-active {
		background: #db7842 !important;
		color: rgba(255, 255, 255, 1) !important;

		.v-btn__content {
			opacity: 1 !important;
		}
	}

	&__editor {
		padding: 0;
	}

	.ql-container {
		border: none !important;
		order: 1;
		font-size: 17px !important;
		font-family: 'Montserrat' !important;
	}

	.ql-toolbar {
		padding-top: 30px !important;
		border: none !important;
		padding-left: 0 !important;
		order: 2;
	}
}

.form-control {
	&:focus {
		outline: none;
	}
}
</style>
